import { ref } from 'vue';

const createImageLoader = () => {
  return url =>
    new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load image'));

      img.src = url;
    });
};

const createMediaLoader = mediaTag => {
  return url =>
    new Promise((resolve, reject) => {
      const media = document.createElement(mediaTag);

      const cleanup = () => {
        media.onerror = null;
        media.onloadeddata = null;
        media.src = '';
        media.load?.();
      };

      media.onloadeddata = () => {
        cleanup();
        resolve();
      };

      media.onerror = () => {
        cleanup();
        reject(new Error(`Failed to load ${mediaTag}`));
      };

      media.src = url;
      media.load?.();
    });
};

const createFetchLoader = () => {
  return async url => {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error('Failed to load resource');
    }
  };
};

export const useLoadWithRetry = (config = {}) => {
  const maxRetry = config.max_retry || config.maxRetry || 3;
  const backoff = config.backoff || 1000;
  const loaderType = config.type || 'image';
  const customLoader = config.loader;
  const loadedCache = new Set();

  const isLoaded = ref(false);
  const hasError = ref(false);
  const loadedSrc = ref(null);

  const loaders = {
    image: createImageLoader(),
    video: createMediaLoader('video'),
    audio: createMediaLoader('audio'),
  };

  const loadResource =
    customLoader || loaders[loaderType] || createFetchLoader();

  const sleep = ms =>
    new Promise(resolve => {
      setTimeout(resolve, ms);
    });

  const retry = async (url, attempt = 0) => {
    try {
      await loadResource(url);
      isLoaded.value = true;
      hasError.value = false;
      loadedSrc.value = url;
      loadedCache.add(url);
      return true;
    } catch (error) {
      if (attempt + 1 >= maxRetry) {
        return false;
      }
      await sleep(backoff * (attempt + 1));
      return retry(url, attempt + 1);
    }
  };

  const loadWithRetry = async urls => {
    const sources = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
    if (!sources.length) {
      hasError.value = true;
      isLoaded.value = false;
      loadedSrc.value = null;
      return null;
    }

    // Avoid flicker/reload if we already loaded one of the sources.
    if (
      isLoaded.value &&
      loadedSrc.value &&
      sources.includes(loadedSrc.value)
    ) {
      return loadedSrc.value;
    }

    const cachedSource = sources.find(src => loadedCache.has(src));
    if (cachedSource) {
      isLoaded.value = true;
      hasError.value = false;
      loadedSrc.value = cachedSource;
      return cachedSource;
    }

    isLoaded.value = false;
    hasError.value = false;
    loadedSrc.value = null;

    const resolvedSrc = await sources.reduce(async (previousPromise, src) => {
      const previousResult = await previousPromise;
      if (previousResult) return previousResult;

      const loaded = await retry(src);
      return loaded ? src : null;
    }, Promise.resolve(null));

    if (resolvedSrc) {
      return resolvedSrc;
    }

    hasError.value = true;
    isLoaded.value = false;
    loadedSrc.value = null;
    return null;
  };

  return {
    isLoaded,
    hasError,
    loadedSrc,
    loadWithRetry,
  };
};
